import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom-v5-compat';
import moment from 'moment';

import { focusElement } from 'platform/utilities/ui';
import { scrollToTop } from 'platform/utilities/scroll';
import CallVBACenter from '@department-of-veterans-affairs/platform-static-data/CallVBACenter';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';

import {
  APPEAL_TYPES,
  EVENT_TYPES,
  getTypeName,
} from '~/platform/shared/claims-appeals';
import {
  clearNotification as clearNotificationAction,
  getAppealsV2 as getAppealsV2Action,
} from '../actions';
import { clearSCUploadErrors } from '../actions/submitClaimsEvidenceFiles';
import manifest from '../manifest.json';
import AppealNotFound from '../components/appeals-v2/AppealNotFound';
import AppealHeader from '../components/appeals-v2/AppealHeader';
import AppealsV2TabNav from '../components/appeals-v2/AppealsV2TabNav';
import AppealHelpSidebar from '../components/appeals-v2/AppealHelpSidebar';
import ClaimsBreadcrumbs from '../components/ClaimsBreadcrumbs';
import CopyOfExam from '../components/CopyOfExam';
import Notification from '../components/Notification';
import ServiceUnavailableAlert from '../components/ServiceUnavailableAlert';
import TabbedDetail from '../components/TabbedDetail';
import { ANCHOR_LINKS, DEFAULT_APPEAL_TAB } from '../constants';
import { recordAppealTabChangeEvent } from '../utils/analytics';
import { focusNotificationAlert, setPageFocus, setUpPage } from '../utils/page';
import withRouter from '../utils/withRouter';
import { setTabDocumentTitle } from '../utils/helpers';

import {
  getAppealTabs,
  isolateAppeal,
  searchWithTab,
  tabForSubpath,
} from '../utils/appeals-v2-helpers';

import { RECORD_NOT_FOUND_ERROR } from '../actions/types';
import AppealsV2StatusPage from './AppealsV2StatusPage';
import AppealsV2DetailPage from './AppealsV2DetailPage';
import SupplementalClaimFilesPage from './SupplementalClaimFilesPage';
import SupplementalClaimUploadErrorAlert from '../components/appeals-v2/SupplementalClaimUploadErrorAlert';

const AVAILABLE = 'AVAILABLE';
const FILES_RECEIVED_ANCHOR = `#${ANCHOR_LINKS.filesReceived}`;

const lastPathSegment = pathname =>
  (pathname || '').split('/').filter(Boolean).pop();

// The currently active tab, from either URL shape:
//
//   /appeals/:id?tab=files   new tabs (va-tabs)
//   /appeals/:id/files       old tabs (route-based)
//
// Old-tab URLs call the Issues tab `detail`; normalised to `issues` so callers
// only learn one name.
function getCurrentTabId({ useVaTabs, searchParams, pathname }) {
  if (useVaTabs) {
    return searchParams?.get('tab') ?? null;
  }

  const last = lastPathSegment(pathname);

  if (last === 'detail') {
    return 'issues';
  }

  return last ?? null;
}

const capitalizeWord = word => {
  const capFirstLetter = word[0].toUpperCase();
  return `${capFirstLetter}${word.slice(1)}`;
};

const recordsNotFoundMessage = (
  <div className="row" id="recordsNotFoundMessage">
    <div className="small-12 columns">
      <div className="react-container">
        <h3>We’re sorry. We can’t find your records in our system.</h3>
        <p>
          If you think they should be here, please try again later or{' '}
          <CallVBACenter />
        </p>
      </div>
    </div>
  </div>
);

export class AppealInfo extends React.Component {
  state = {
    // Used to focus the "Files received" heading after the success-alert link
    // switches tabs. Set on click, cleared once the Files tab renders.
    shouldFocusFilesReceived: false,
  };

  // Used to focus the appeal h1 on arrival, once. Stops it re-firing on tab
  // changes and on the post-upload appeals re-fetch.
  hasFocusedOnArrival = false;

  componentDidMount() {
    const { appeal, getAppealsV2, location, searchParams, useVaTabs } =
      this.props;

    if (!appeal) {
      getAppealsV2();
    }

    const nowTab = getCurrentTabId({
      useVaTabs,
      searchParams,
      pathname: location?.pathname,
    });

    if (appeal?.id && nowTab) {
      setTabDocumentTitle(appeal, nowTab);
    }

    // Unconditional: the incoming scroll offset is the claims list's, and
    // history.scrollRestoration is 'manual' app-wide.
    scrollToTop({ behavior: 'instant' });
    this.focusOnArrival();
  }

  componentDidUpdate(prevProps) {
    const {
      appeal,
      clearNotification,
      location,
      message,
      params,
      searchParams,
      useVaTabs,
    } = this.props;

    const nowTab = getCurrentTabId({
      useVaTabs,
      searchParams,
      pathname: location?.pathname,
    });

    const prevTab = getCurrentTabId({
      useVaTabs: prevProps.useVaTabs,
      searchParams: prevProps.searchParams,
      pathname: prevProps.location?.pathname,
    });

    const pathChanged = prevProps.params?.id !== params?.id;
    const appealChanged = appeal !== prevProps.appeal;
    const tabChanged = !!nowTab && !!prevTab && nowTab !== prevTab;
    const tabResolved = !prevTab && !!nowTab;

    // Clear the alert when the user navigates between appeals without
    // remounting AppealInfo.
    if (pathChanged) {
      this.props.clearSCErrors();
      clearNotification();
      this.hasFocusedOnArrival = false;
    }

    if (pathChanged || appealChanged || tabChanged || tabResolved) {
      setTabDocumentTitle(appeal, nowTab);
    }

    this.focusOnArrival();

    // Focus the alert when it first appears (post-upload).
    if (!prevProps.message && message) {
      requestAnimationFrame(focusNotificationAlert);
    }

    // Priority path: the success-alert link asked us to jump to "Files
    // received" specifically. Clear it once the Files tab arrives.
    if (
      this.state.shouldFocusFilesReceived &&
      nowTab === 'files' &&
      prevTab !== 'files'
    ) {
      this.setState({ shouldFocusFilesReceived: false });
      requestAnimationFrame(() => {
        setPageFocus(FILES_RECEIVED_ANCHOR);
      });
    } else if (tabChanged && message) {
      // Alert present: pull focus to its h2 so the upload confirmation is
      // announced on the new tab. Without an alert focus is deliberately left
      // alone — the tab control keeps it (old tabs: NavLink; new tabs:
      // TabbedDetail restores the va-tab-item), so arrow keys keep working.
      requestAnimationFrame(focusNotificationAlert);
    }
  }

  // Shared notifications slice — without this the SC alert renders on claim pages.
  componentWillUnmount() {
    this.props.clearNotification();
    this.props.clearSCErrors();
  }

  // The canonical URL for the active tab, or null when already canonical. New
  // tabs only: every tab lives at /appeals/:id?tab=<id>, so a bare path, an
  // unknown ?tab=, or any path-style subpath redirects.
  getCanonicalTabRedirect = () => {
    const {
      appeal,
      appealsLoading,
      cstSupplementalClaimsEvidenceUpload,
      location,
      params,
      searchParams,
      useVaTabs,
    } = this.props;
    if (!useVaTabs || appealsLoading || !appeal) {
      return null;
    }

    const lastSegment = lastPathSegment(location.pathname);
    const onBarePath = lastSegment === params.id;
    const appealTabs = getAppealTabs(appeal, {
      cstSupplementalClaimsEvidenceUpload,
    });
    const knownTab = appealTabs.some(tab => tab.id === searchParams.get('tab'));

    if (onBarePath && knownTab) {
      return null;
    }

    const tab = onBarePath
      ? DEFAULT_APPEAL_TAB
      : tabForSubpath(lastSegment, appealTabs);
    return `/appeals/${params.id}${searchWithTab(location.search, tab)}`;
  };

  // Move focus to the appeal heading when the veteran first lands on the page.
  // The guards below wait for it, and the flag is only set once focus has
  // actually landed.
  focusOnArrival = () => {
    const { appeal, appealsAvailability, appealsLoading } = this.props;

    if (this.hasFocusedOnArrival) {
      return;
    }

    // appealsAvailability stays undefined until a fetch resolves.
    const settled = !appealsLoading && !!(appeal || appealsAvailability);

    if (!settled || this.getCanonicalTabRedirect()) {
      return;
    }

    if (!document.querySelector('h1')) {
      return;
    }

    this.hasFocusedOnArrival = true;
    setUpPage(false, 'h1');
  };

  createHeading = () => {
    let requestEventType;
    const { appeal } = this.props;
    switch (appeal.type) {
      case APPEAL_TYPES.legacy:
        requestEventType = EVENT_TYPES.nod;
        break;
      case APPEAL_TYPES.supplementalClaim:
        requestEventType = EVENT_TYPES.scRequest;
        break;
      case APPEAL_TYPES.higherLevelReview:
        requestEventType = EVENT_TYPES.hlrRequest;
        break;
      case APPEAL_TYPES.appeal:
        requestEventType = EVENT_TYPES.amaNod;
        break;
      default:
      // do nothing
    }
    const requestEvent = appeal.attributes.events.find(
      event => event.type === requestEventType,
    );

    let appealTitle = capitalizeWord(getTypeName(appeal));

    if (requestEvent) {
      appealTitle += ` received ${moment(requestEvent.date).format(
        'MMMM YYYY',
      )}`;
    }

    return appealTitle;
  };

  // New tabs: push the selected tab into the URL so back/forward and refresh keep
  // the active tab. The URL is always bare path + ?tab=, so navigating by search
  // alone preserves it.
  handleTabChange = nextTabId => {
    const { navigate, location } = this.props;
    recordAppealTabChangeEvent({ tab: nextTabId });
    navigate({ search: searchWithTab(location.search, nextTabId) });
  };

  // Success-alert link: switch to Files, then focus "Files received" once the
  // tab renders (see componentDidUpdate). Works with old and new tabs.
  handleFilesReceivedLinkClick = e => {
    // A modified click is the user asking the browser for a new tab/window;
    // fall through to the href rather than swallowing it.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    const { location, navigate, searchParams, useVaTabs } = this.props;
    const onFilesTab =
      getCurrentTabId({
        useVaTabs,
        searchParams,
        pathname: location?.pathname,
      }) === 'files';
    if (onFilesTab) {
      setPageFocus(FILES_RECEIVED_ANCHOR);
      return;
    }
    this.setState({ shouldFocusFilesReceived: true });
    if (useVaTabs) {
      // Not filesTabRoute: this preserves other query params and records the
      // tab-change analytics event.
      this.handleTabChange('files');
    } else {
      navigate(this.filesTabRoute());
    }
  };

  // The Files tab as a router path — basename-relative, so it is ready for
  // navigate() but needs manifest.rootUrl prefixed to be used as an href.
  filesTabRoute = () => {
    const { params, useVaTabs } = this.props;
    return useVaTabs
      ? `/appeals/${params.id}?tab=files`
      : `/appeals/${params.id}/files`;
  };

  renderUploadSuccessAlertBody = message => {
    const isPlural = message.fileCount !== 1;
    const noun = isPlural ? 'documents' : 'document';
    const linkText = isPlural
      ? 'Check which files we received'
      : 'Check which file we received';
    const href = `${
      manifest.rootUrl
    }${this.filesTabRoute()}${FILES_RECEIVED_ANCHOR}`;
    return (
      <>
        <p className="vads-u-margin-y--0">
          We received {message.fileCount} {noun} from you on{' '}
          <strong>{message.uploadedAtLabel}</strong>.
        </p>
        <va-link
          class="vads-u-display--block vads-u-margin-top--2"
          href={href}
          text={linkText}
          onClick={this.handleFilesReceivedLinkClick}
        />
      </>
    );
  };

  render() {
    const {
      appeal,
      appealsAvailability,
      appealsLoading,
      clearNotification,
      cstSupplementalClaimsEvidenceUpload,
      fullName,
      message,
      searchParams,
      scFailedFiles,
      scUploading,
      useVaTabs,
    } = this.props;

    // New tabs: every tab gets one canonical URL (?tab=<id>).
    const canonicalTabRedirect = this.getCanonicalTabRedirect();
    if (canonicalTabRedirect) {
      return <Navigate to={canonicalTabRedirect} replace />;
    }

    const appealTabs = getAppealTabs(appeal, {
      cstSupplementalClaimsEvidenceUpload,
    });
    const activeTab = useVaTabs ? searchParams.get('tab') : null;

    let appealContent;
    let claimHeading;

    // Availability is determined by whether or not the API returned an appeals array
    // for this user. However, it doesn't speak to whether the appeal that's been
    // requested is available in the array. This is why we have to check for both
    // AVAILABLE status as well as whether or not the appeal exists.
    // `&& !appeal`: only block the page when there is nothing to show yet.
    // FETCH_APPEALS_PENDING doesn't clear the appeal, so a background refresh
    // (the post-upload one) keeps the heading, tabs and file list on screen
    // instead of flashing a spinner over content we already have.
    if (appealsLoading && !appeal) {
      appealContent = (
        <div className="vads-u-margin-bottom--2p5">
          <va-loading-indicator message="Please wait while we load your appeal..." />
        </div>
      );
    } else if (appealsAvailability === AVAILABLE && appeal) {
      // Maybe could simplify this to just check if (appeal) instead
      claimHeading = this.createHeading();
      if (useVaTabs) {
        const tabContent = {
          status: <AppealsV2StatusPage appeal={appeal} fullName={fullName} />,
          issues: <AppealsV2DetailPage appeal={appeal} />,
          files: <SupplementalClaimFilesPage appeal={appeal} />,
        };
        appealContent = (
          <TabbedDetail
            label="Appeal"
            activeTabId={activeTab}
            tabs={appealTabs.map(tab => ({
              ...tab,
              content: tabContent[tab.id],
            }))}
            onTabChange={this.handleTabChange}
          />
        );
      } else {
        appealContent = (
          <>
            <AppealsV2TabNav appeal={appeal} />
            <div className="tab-content va-appeals-content">
              <Outlet context={[appeal, fullName]} />
            </div>
          </>
        );
      }
    } else if (appealsAvailability === AVAILABLE && !appeal) {
      // Yes, we have your appeals. No, the one you requested isn't one of them.
      appealContent = <AppealNotFound />;
    } else if (appealsAvailability === RECORD_NOT_FOUND_ERROR) {
      appealContent = recordsNotFoundMessage;
    } else {
      // This includes
      //  USER_FORBIDDEN_ERROR,
      //  VALIDATION_ERROR,
      //  BACKEND_SERVICE_ERROR,
      //  FETCH_APPEALS_ERROR
      appealContent = (
        <>
          <h1>We encountered a problem</h1>
          <ServiceUnavailableAlert
            headerLevel={2}
            services={['appeals']}
            useSingular
          />
        </>
      );
    }

    const crumb = {
      href: `../status`,
      label: 'Status details',
      isRouterLink: true,
    };

    return (
      <div>
        <div className="row">
          <div className="usa-width-two-thirds medium-8 column">
            <ClaimsBreadcrumbs crumbs={[crumb]} />
          </div>
        </div>
        <div className="row">
          <div className="usa-width-two-thirds medium-8 column">
            {!!(claimHeading && appeal) && (
              <AppealHeader
                heading={claimHeading}
                lastUpdated={appeal.attributes.updated}
              />
            )}
            <SupplementalClaimUploadErrorAlert
              scFailedFiles={scFailedFiles}
              uploading={scUploading}
            />
            {message && (
              <div className="vads-u-margin-top--3">
                <Notification
                  title={message.title}
                  body={
                    message.kind === 'sc-upload-success'
                      ? this.renderUploadSuccessAlertBody(message)
                      : message.body
                  }
                  type={message.type}
                  onClose={() => {
                    focusElement('h1');
                    clearNotification();
                  }}
                />
              </div>
            )}
            {appealContent}
            {appeal && (
              <AppealHelpSidebar
                location={appeal.attributes.location}
                aoj={appeal.attributes.aoj}
              />
            )}
            <CopyOfExam />
          </div>
        </div>
      </div>
    );
  }
}

AppealInfo.propTypes = {
  appealsLoading: PropTypes.bool.isRequired,
  getAppealsV2: PropTypes.func.isRequired,
  params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  appeal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    attributes: PropTypes.object.isRequired,
  }),
  appealsAvailability: PropTypes.string,
  clearNotification: PropTypes.func,
  clearSCErrors: PropTypes.func,
  cstSupplementalClaimsEvidenceUpload: PropTypes.bool,
  fullName: PropTypes.shape({
    first: PropTypes.string,
    middle: PropTypes.string,
    last: PropTypes.string,
  }),
  // Provided by withRouter; only read on the flag-on (va-tabs) path.
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  message: PropTypes.shape({
    body: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    title: PropTypes.string,
    type: PropTypes.string,
  }),
  navigate: PropTypes.func,
  scFailedFiles: PropTypes.arrayOf(
    PropTypes.shape({ fileName: PropTypes.string, fileType: PropTypes.string }),
  ),
  scUploading: PropTypes.bool,
  searchParams: PropTypes.shape({ get: PropTypes.func.isRequired }),
  useVaTabs: PropTypes.bool,
};

// Tests render the unconnected component without this prop, so the default
// keeps the call sites free of optional chaining.
AppealInfo.defaultProps = {
  clearNotification: () => {},
  clearSCErrors: () => {},
};

function mapStateToProps(state, ownProps) {
  const { appealsLoading, v2Availability: appealsAvailability } =
    state.disability.status.claimsV2;

  return {
    appeal: isolateAppeal(state, ownProps.params.id),
    appealsLoading,
    appealsAvailability,
    fullName: state.user.profile.userFullName,
    message: state.disability.status.notifications.message,
    scFailedFiles: state?.disability?.status?.uploads?.failedFiles,
    scUploading: state?.disability?.status?.uploads?.uploading,
    useVaTabs: toggleValues(state)[FEATURE_FLAG_NAMES.cstAppealDetailsVaTabs],
    cstSupplementalClaimsEvidenceUpload:
      toggleValues(state)[
        FEATURE_FLAG_NAMES.cstSupplementalClaimsEvidenceUpload
      ],
  };
}

const mapDispatchToProps = {
  clearNotification: clearNotificationAction,
  clearSCErrors: clearSCUploadErrors,
  getAppealsV2: getAppealsV2Action,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AppealInfo),
);
